import React, { useEffect, useState, useRef } from "react";
import CustomInput from "../components/CustomInput";
import ReactQuill from "react-quill";
import { useNavigate } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import axios from "axios";
import { base_url } from "../utils/baseUrl";
import { Select } from "antd";

const AddProduct = () => {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    price: "",
    category: [], // Default to an empty array for categories
    sku: "",
    quantity: "",
    discount: "",
  });
  const [attributes, setAttributes] = useState([{ key: "", value: "" }]);
  const [sizes, setSizes] = useState([{ size: "", price: "", quantity: "" }]);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productDescriptions, setProductDescriptions] = useState([{ key: "", value: "" }]);
  const [attributeNames, setAttributeNames] = useState([]);
  const [attributeValues, setAttributeValues] = useState({}); // Store values for each attribute

  // Fetch categories from the backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${base_url}/api/categories/getAllCategories`);
        setCategories(response.data.categoriesWithImages);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  // Fetch attribute names from the backend
  useEffect(() => {
    const fetchAttributeNames = async () => {
      try {
        const response = await axios.get(`${base_url}/api/attributes/names`);
        setAttributeNames(response.data);
      } catch (error) {
        console.error("Error fetching attribute names:", error);
        toast.error("Failed to load attribute names");
      }
    };
    fetchAttributeNames();
  }, []);

  const adminToken = localStorage.getItem("adminToken");
  console.log("Admin Token:", adminToken);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) {
      navigate("/");
    }
  }, [navigate]);

  // Fetch values for a specific attribute
  const fetchAttributeValues = async (attributeId, index) => {
    try {
      const response = await axios.get(`${base_url}/api/attributes/values/${attributeId}`);
      setAttributeValues((prevValues) => ({
        ...prevValues,
        [index]: response.data.map((item) => item.value),
      }));
    } catch (error) {
      console.error("Error fetching attribute values:", error);
      toast.error("Failed to load attribute values");
    }
  };

  // Handle attribute key change
  const handleAttributeKeyChange = (index, value) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[index].key = value;
    updatedAttributes[index].value = ""; // Reset value when key changes
    setAttributes(updatedAttributes);

    // Fetch values for the selected attribute
    const selectedAttribute = attributeNames.find((attr) => attr.name === value);
    if (selectedAttribute) {
      fetchAttributeValues(selectedAttribute.attribute_id, index);
    }
  };

  // Handle adding/removing sizes
  const handleAddSize = () => {
    setSizes([...sizes, { size: "", price: "", quantity: "" }]);
  };

  const handleRemoveSize = (index) => {
    setSizes(sizes.filter((_, i) => i !== index));
  };

  const calculateTotalQuantity = (sizes) => {
    return sizes.reduce((total, size) => total + (parseInt(size.quantity) || 0), 0);
  };

  const handleSizeChange = (index, field, value) => {
    const updatedSizes = [...sizes];
    updatedSizes[index][field] = value;
    setSizes(updatedSizes);

    // Automatically update the total product quantity
    const totalQuantity = calculateTotalQuantity(updatedSizes);
    setFormValues((prevValues) => ({
      ...prevValues,
      quantity: totalQuantity,
    }));
  };

  // Handle image upload
  const handleImageClick = () => {
    inputRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    const imagePreviews = [];
    files.forEach((file) => {
      // Check if file is an image
      if (file.type.startsWith('image')) {
        imagePreviews.push({
          file: file,
          url: URL.createObjectURL(file),
        });
      }
    });

    setImages((prevImages) => [...prevImages, ...imagePreviews]);
  };

  const handleImageRemove = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      description: content,
    }));
  };

  // Handle attributes
  const handleAddAttribute = () => {
    setAttributes([...attributes, { key: "", value: "" }]);
  };

  const handleRemoveAttribute = (index) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const handleAttributeChange = (index, field, value) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[index][field] = value;

    // If the field is "value" and the value is "__new", initialize the input for custom value
    if (field === "value" && value === "__new") {
      updatedAttributes[index].customValue = ""; // Add a customValue field for new input
    }

    setAttributes(updatedAttributes);
  };

  // Handle product descriptions (key-value pairs)
  const handleAddDescription = () => {
    setProductDescriptions([...productDescriptions, { key: "", value: "" }]);
  };

  const handleRemoveDescription = (index) => {
    setProductDescriptions(productDescriptions.filter((_, i) => i !== index));
  };

  const handleDescriptionChangeValue = (index, field, value) => {
    const updatedDescriptions = [...productDescriptions];
    updatedDescriptions[index][field] = value;
    setProductDescriptions(updatedDescriptions);
  };

  const handleCategoryChange = (selectedCategories) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      category: selectedCategories,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Process attributes to include custom values if any
    const processedAttributes = attributes.map((attr) => ({
      key: attr.key,
      value: attr.value === "__new" ? attr.customValue : attr.value, // Use customValue if "__new" is selected
    }));

    // Basic required field validation
    const requiredFields = ["name", "description", "price", "category", "quantity"];
    for (let field of requiredFields) {
      if (!formValues[field] || formValues[field].length === 0) {
        toast.error(`Please enter a valid ${field}`);
        return;
      }
    }

    // Validate sizes
    for (let size of sizes) {
      if (!size.size || size.size.trim() === "" || !size.price || size.price.trim() === "" || !size.quantity || size.quantity.trim() === "") {
        toast.error("Please fill out all size, price, and quantity fields.");
        return;
      }
    }

    // Validate attributes
    for (let attr of attributes) {
      if (!attr.key || attr.key.trim() === "" || !attr.value || attr.value.trim() === "") {
        toast.error("Please fill out all attribute key and value fields.");
        return;
      }
    }

    // Validate product descriptions
    for (let desc of productDescriptions) {
      if (!desc.key || desc.key.trim() === "" || !desc.value || desc.value.trim() === "") {
        toast.error("Please fill out all product description key and value fields.");
        return;
      }
    }

    if (images.length === 0) {
      toast.error("Please upload at least one image.");
      return;
    }

    const formData = new FormData();
    images.forEach((image) => formData.append("images", image.file));
    formData.append("attributes", JSON.stringify(processedAttributes)); // Use processedAttributes here
    formData.append("sizes", JSON.stringify(sizes));
    formData.append("productDescriptions", JSON.stringify(productDescriptions));
    Object.keys(formValues).forEach((key) => formData.append(key, formValues[key]));
    formData.append("categories", JSON.stringify(formValues.category)); // Append multiple categories

    try {
      const response = await axios.post(`${base_url}/api/products/create`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Product Added Successfully!");
      window.location.href = `https://gulabidecor.com/product/${formValues.name}`;
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error("Something Went Wrong!");
    }
  };


  return (
    <div>
      <h3 className="mb-4 title">Add Product</h3>
      <form onSubmit={handleSubmit} className="d-flex gap-3 flex-column">
        {/* Product Name */}
        <CustomInput
          type="text"
          label="Enter Product Name"
          name="name"
          onChng={handleInputChange}
          val={formValues.name}
          required // Add this
        />

        {/* Description */}
        <ReactQuill
          theme="snow"
          value={formValues.description}
          onChange={handleDescriptionChange}
        />

        {/* Price, Quantity, Discount */}
        <CustomInput
          type="number"
          label="Enter Price"
          name="price"
          onChng={handleInputChange}
          val={formValues.price}
          required // Add this
        />
        <CustomInput
          type="number"
          label="Total Quantity"
          name="quantity"
          val={formValues.quantity}
          readOnly // Make the quantity field readonly
        />
        <CustomInput
          type="number"
          label="Enter Discount"
          name="discount"
          onChng={handleInputChange}
          val={formValues.discount}
        // Add this
        />

        {/* Sizes Section */}
        <div>
          <label>Sizes, Prices, and Quantities</label>
          {sizes.map((size, index) => (
            <div key={index} className="d-flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Size"
                className="form-control"
                value={size.size}
                onChange={(e) => handleSizeChange(index, "size", e.target.value)}

              />
              <input
                type="number"
                placeholder="Price"
                className="form-control"
                value={size.price}
                onChange={(e) => handleSizeChange(index, "price", e.target.value)}
              // Add this
              />
              <input
                type="number"
                placeholder="Quantity"
                className="form-control"
                value={size.quantity}
                onChange={(e) => handleSizeChange(index, "quantity", e.target.value)}
              />
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleRemoveSize(index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-success" onClick={handleAddSize}>
            Add Size
          </button>
        </div>

        {/* Category Selection */}
        <label>Select Categories</label>
        <Select
          mode="multiple" // Allow multiple selection
          placeholder="Select categories"
          value={formValues.category} // Ensure no categories are selected by default
          onChange={handleCategoryChange}
          style={{ width: "100%" }}
        >
          {categories.map((category) => (
            <Select.Option key={category.category_id} value={category.category_id}>
              {category.name}
            </Select.Option>
          ))}
        </Select>

        {/* Attributes Section */}
        <div>
          <label>Attributes</label>
          {attributes.map((attr, index) => (
            <div key={index} className="d-flex gap-2 mb-3">
              <select
                className="form-control"
                value={attr.key}
                onChange={(e) => handleAttributeKeyChange(index, e.target.value)}
              >
                <option value="">Select Attribute</option>
                {attributeNames.map((attribute) => (
                  <option key={attribute.attribute_id} value={attribute.name}>
                    {attribute.name}
                  </option>
                ))}
              </select>
              <select
                className="form-control"
                value={attr.value}
                onChange={(e) => handleAttributeChange(index, "value", e.target.value)}
              >
                <option value="">Select Value</option>
                {attributeValues[index]?.map((value, i) => (
                  <option key={i} value={value}>
                    {value}
                  </option>
                ))}
                <option value="__new">Add New Value</option>
              </select>
              {attr.value === "__new" && (
                <input
                  type="text"
                  placeholder="Enter Custom Value"
                  className="form-control"
                  value={attr.customValue || ""} // Use customValue for the input
                  onChange={(e) => handleAttributeChange(index, "customValue", e.target.value)}
                />
              )}
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleRemoveAttribute(index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-success" onClick={handleAddAttribute}>
            Add Attribute
          </button>
        </div>

        {/* Product Descriptions Section (Key-Value Pairs) */}
        <div>
          <label>Product Descriptions (Key-Value)</label>
          {productDescriptions.map((desc, index) => (
            <div key={index} className="d-flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Key"
                className="form-control"
                value={desc.key}
                onChange={(e) =>
                  handleDescriptionChangeValue(index, "key", e.target.value)
                }
              // Add this
              />
              <input
                type="text"
                placeholder="Value"
                className="form-control"
                value={desc.value}
                onChange={(e) =>
                  handleDescriptionChangeValue(index, "value", e.target.value)
                }
              // Add this
              />
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleRemoveDescription(index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-success" onClick={handleAddDescription}>
            Add Description
          </button>
        </div>

        {/* Images Section */}
        <div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleImageClick}
          >
            Upload Images
          </button>
          <input
            type="file"
            ref={inputRef}
            style={{ display: "none" }}
            multiple
            accept="image/*"
            onChange={handleFileChange}
          />
          <div className="image-preview-container">
            {images.map((image, index) => (
              <div key={index} className="image-preview">
                <img src={image.url} alt="Product" className="img-thumbnail" />
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleImageRemove(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <button type="submit" className="btn btn-success mt-4">
          Add Product
        </button>
      </form>
    </div>
  );
};

export default AddProduct;
